import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import * as React from 'react'

import IconButton from '../../../../common/IconButton/IconButton'
import type {LogView} from '../../BuildLog.types'
import {Status} from '../../BuildLog.types'
import {getTextWithSpaceIndents, leftPad} from '../BuildLogMessages.utils'

import LinkedMessage from './LinkedMessage/LinkedMessage'
import LogMessageDuration from './LogMessageDuration/LogMessageDuration'
import LogMessageSearchHighlighting from './LogMessageSearchHighlighting/LogMessageSearchHighlighting'
import LogMessageText from './LogMessageText/LogMessageText'

import styles from './LogMessage.css'

type Props = {
  className?: string | null | undefined
  treeView?: boolean
  id: number
  level: number
  expandable?: boolean | null | undefined
  expanded?: boolean | null | undefined
  text: string
  duration?: number | null | undefined
  status: Status
  childrenEmpty?: boolean
  childrenLoading?: boolean
  breakLines?: boolean
  parseLinks?: boolean | null | undefined
  parseAnsi?: boolean | null | undefined
  linkedMessageId?: number | null | undefined
  verbose?: boolean | null | undefined
  onMessageToggle?: (
    index: number,
    expand: boolean,
    withFocus?: boolean,
    childrenEmpty?: boolean | null,
  ) => unknown
  role?: string
  onLinkedMessageClick?: (event: React.MouseEvent<any>, id: number) => unknown
  searchQuery?: string | null | undefined
  searchMatchPosition?: number | null
  scrollToSearch?: boolean | null
  showDuration: boolean
  timestamp?: string
  logView?: LogView
}

class LogMessage extends React.PureComponent<Props> {
  onChevronClick: (event: React.MouseEvent<EventTarget> | null | undefined) => void = (
    event: React.MouseEvent<any> | null | undefined,
  ) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const {onMessageToggle, id, expanded, childrenEmpty} = this.props
    onMessageToggle?.(id, !expanded, false, childrenEmpty)
  }

  render(): React.ReactNode {
    const {
      status,
      text,
      duration,
      level,
      treeView,
      expandable,
      expanded,
      breakLines,
      parseLinks,
      parseAnsi,
      className,
      verbose,
      role,
      childrenLoading,
      linkedMessageId,
      onLinkedMessageClick,
      searchQuery,
      searchMatchPosition,
      scrollToSearch,
      showDuration,
      timestamp,
      logView,
    } = this.props
    const spaces = leftPad(level)
    const textWithSpaceIndents = getTextWithSpaceIndents(text, spaces)
    return (
      <div
        className={classNames(styles.message, className, {
          [styles.error]: status === Status.ERROR || status === Status.TRACE_ERROR,
          [styles.warning]: status === Status.WARNING,
          [styles.verbose]: verbose,
          [styles.breakLines]: breakLines,
          [styles.treeMessage]: treeView,
        })}
        role={role}
        data-log-message
        data-test-log-message
      >
        <div className={styles.text}>
          <LogMessageText
            text={textWithSpaceIndents}
            parseLinks={parseLinks}
            parseAnsi={parseAnsi}
          />
          {searchQuery != null && (
            <LogMessageSearchHighlighting
              text={textWithSpaceIndents}
              searchQuery={searchQuery}
              className={styles.searchHighlighiting}
              searchMatchPosition={searchMatchPosition}
              scrollToSearch={scrollToSearch}
            />
          )}
          {linkedMessageId != null && onLinkedMessageClick != null && (
            <>
              {' '}
              <LinkedMessage
                linkedMessageId={linkedMessageId}
                onClick={onLinkedMessageClick}
                logView={logView}
              />
            </>
          )}
          {showDuration && (
            <LogMessageDuration
              duration={duration}
              className={styles.duration}
              timestamp={timestamp}
            />
          )}
        </div>
        {treeView === true && expandable === true && childrenLoading !== true && (
          <IconButton
            special
            title={expanded === true ? 'Collapse' : 'Expand'}
            className={styles.collapseButton}
            iconWrapperClassName={styles.collapseIcon}
            icon={expanded === true ? 'collapse-14px' : 'expand-14px'}
            childrenLeft
            onClick={this.onChevronClick}
            data-test-log-message-toggle
          >
            {spaces}
          </IconButton>
        )}
        {childrenLoading === true && (
          <span className={styles.loaderWrapper}>
            {spaces}
            <LoaderInline className={styles.loader} />
          </span>
        )}
      </div>
    )
  }
}

export default LogMessage
